import type { AssetId, ChainId } from '@shapeshiftoss/caip'
import {
  ASSET_NAMESPACE,
  ASSET_REFERENCE,
  solanaChainId,
  solAssetId,
  toAssetId,
} from '@shapeshiftoss/caip'
import type {
  HDWallet,
  SolanaAddressLookupTableAccountInfo,
  SolanaSignTx,
  SolanaTxInstruction,
  SolanaWallet,
} from '@shapeshiftoss/hdwallet-core'
import { supportsSolana } from '@shapeshiftoss/hdwallet-core'
import type { Bip44Params, RootBip44Params } from '@shapeshiftoss/types'
import { KnownChainIds } from '@shapeshiftoss/types'
import * as unchained from '@shapeshiftoss/unchained-client'
import { bn, bnOrZero } from '@shapeshiftoss/utils'
import {
  createAssociatedTokenAccountInstruction,
  createTransferCheckedInstruction,
  createTransferInstruction,
  getAccount,
  getAssociatedTokenAddressSync,
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
  TokenAccountNotFoundError,
  TokenInvalidAccountOwnerError,
} from '@solana/spl-token'
import type { AccountInfo, TransactionInstruction } from '@solana/web3.js'
import {
  AddressLookupTableAccount,
  ComputeBudgetProgram,
  Connection,
  PublicKey,
  SystemProgram,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import { isUndefined } from 'lodash'
import PQueue from 'p-queue'

import type { ChainAdapter as IChainAdapter } from '../api'
import {
  ChainAdapterError,
  ErrorHandler,
  handleBroadcastTransactionError,
} from '../error/ErrorHandler'
import type {
  Account,
  BroadcastTransactionInput,
  BuildSendApiTxInput,
  BuildSendTxInput,
  FeeDataEstimate,
  GetAddressInput,
  GetBip44ParamsInput,
  GetFeeDataInput,
  SignAndBroadcastTransactionInput,
  SignTx,
  SignTxInput,
  SubscribeError,
  SubscribeTxsInput,
  Transaction,
  TxHistoryInput,
  TxHistoryResponse,
  ValidAddressResult,
} from '../types'
import { ChainAdapterDisplayName, CONTRACT_INTERACTION, ValidAddressResultType } from '../types'
import { toAddressNList, toRootDerivationPath, verifyLedgerAppOpen } from '../utils'
import { assertAddressNotSanctioned } from '../utils/validateAddress'
import { SOLANA_COMPUTE_UNITS_BUFFER_MULTIPLIER } from './constants'
import { isToken2022AccountInfo } from './types'
import { microLamportsToLamports } from './utils'

// Maximum compute units allowed for a single solana transaction
const MAX_COMPUTE_UNITS = 1400000

export interface ChainAdapterArgs {
  providers: {
    http: unchained.solana.Api
    ws: unchained.ws.Client<unchained.solana.Tx>
  }
  rpcUrl: string
}

export class ChainAdapter implements IChainAdapter<KnownChainIds.SolanaMainnet> {
  static readonly rootBip44Params: RootBip44Params = {
    purpose: 44,
    coinType: Number(ASSET_REFERENCE.Solana),
    accountNumber: 0,
  }

  protected readonly chainId = solanaChainId
  protected readonly assetId = solAssetId

  protected readonly providers: {
    http: unchained.solana.Api
    ws: unchained.ws.Client<unchained.solana.Tx>
  }

  protected connection: Connection
  protected parser: unchained.solana.TransactionParser<unchained.solana.Tx>

  constructor(args: ChainAdapterArgs) {
    this.providers = args.providers

    this.connection = new Connection(args.rpcUrl)

    this.parser = new unchained.solana.TransactionParser({
      assetId: this.assetId,
      chainId: this.chainId,
      api: this.providers.http,
    })
  }

  private assertSupportsChain(wallet: HDWallet): asserts wallet is SolanaWallet {
    if (!supportsSolana(wallet)) {
      throw new ChainAdapterError(`wallet does not support: ${this.getDisplayName()}`, {
        translation: 'chainAdapters.errors.unsupportedChain',
        options: { chain: this.getDisplayName() },
      })
    }
  }

  getName() {
    const enumIndex = Object.values(ChainAdapterDisplayName).indexOf(ChainAdapterDisplayName.Solana)
    return Object.keys(ChainAdapterDisplayName)[enumIndex]
  }

  getDisplayName() {
    return ChainAdapterDisplayName.Solana
  }

  getType(): KnownChainIds.SolanaMainnet {
    return KnownChainIds.SolanaMainnet
  }

  getFeeAssetId(): AssetId {
    return this.assetId
  }

  getChainId(): ChainId {
    return this.chainId
  }

  getConnection(): Connection {
    return this.connection
  }

  getBip44Params({ accountNumber }: GetBip44ParamsInput): Bip44Params {
    if (accountNumber < 0) throw new Error('accountNumber must be >= 0')
    return {
      ...ChainAdapter.rootBip44Params,
      accountNumber,
      isChange: false,
      addressIndex: undefined,
    }
  }

  async getAddress(input: GetAddressInput): Promise<string> {
    try {
      const { accountNumber, pubKey, wallet, showOnDevice = false } = input

      if (pubKey) return pubKey

      if (!wallet) throw new Error('wallet is required')
      this.assertSupportsChain(wallet)

      await verifyLedgerAppOpen(this.chainId, wallet)

      const address = await wallet.solanaGetAddress({
        addressNList: toAddressNList(this.getBip44Params({ accountNumber })),
        showDisplay: showOnDevice,
      })

      if (!address) throw new Error('error getting address from wallet')

      return address
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getAddress',
      })
    }
  }

  async getAddresses(wallet: HDWallet, accountNumbers: number[]): Promise<Record<number, string>> {
    try {
      if (!wallet) throw new Error('wallet is required')
      this.assertSupportsChain(wallet)

      await verifyLedgerAppOpen(this.chainId, wallet)

      // Check if wallet supports batch address derivation (Trezor currently, but any wallet could implement solanaGetAddresses)
      if (wallet.solanaGetAddresses) {
        const msgs = accountNumbers.map(accountNumber => ({
          addressNList: toAddressNList(this.getBip44Params({ accountNumber })),
          showDisplay: false,
        }))

        const addresses = await wallet.solanaGetAddresses(msgs)
        return Object.fromEntries(
          accountNumbers.map((accountNumber, i) => [accountNumber, addresses[i]]),
        )
      }

      // Fallback for wallets without batch support
      const addresses = await Promise.all(
        accountNumbers.map(accountNumber => this.getAddress({ accountNumber, wallet })),
      )

      return Object.fromEntries(
        accountNumbers.map((accountNumber, i) => [accountNumber, addresses[i]]),
      )
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getAddress',
      })
    }
  }

  async getAccount(pubkey: string): Promise<Account<KnownChainIds.SolanaMainnet>> {
    try {
      const data = await this.providers.http.getAccount({ pubkey })

      const balance = BigInt(data.balance) + BigInt(data.unconfirmedBalance)

      return {
        balance: balance.toString(),
        chainId: this.chainId,
        assetId: this.assetId,
        chain: this.getType(),
        chainSpecific: {
          tokens: data.tokens.map(token => ({
            assetId: toAssetId({
              chainId: this.chainId,
              assetNamespace: ASSET_NAMESPACE.splToken,
              assetReference: token.id,
            }),
            balance: token.balance,
            name: token.name,
            precision: token.decimals,
            symbol: token.symbol,
          })),
        },
        pubkey,
      }
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getAccount',
        options: { pubkey },
      })
    }
  }

  async getTxHistory(input: TxHistoryInput): Promise<TxHistoryResponse> {
    try {
      const requestQueue = input.requestQueue ?? new PQueue()

      const data = await requestQueue.add(() =>
        this.providers.http.getTxHistory({
          pubkey: input.pubkey,
          pageSize: input.pageSize,
          cursor: input.cursor,
        }),
      )

      const transactions = await Promise.all(
        data.txs.reduce<Promise<Transaction>[]>((prev, tx) => {
          if (input.knownTxIds?.has(tx.txid)) return prev
          prev.push(requestQueue.add(() => this.parseTx(tx, input.pubkey)))
          return prev
        }, []),
      )

      return {
        cursor: data.cursor ?? '',
        pubkey: input.pubkey,
        transactions,
        txIds: data.txs.map(tx => tx.txid),
      }
    } catch (err) {
      return ErrorHandler(err)
    }
  }

  async buildSendApiTransaction(
    input: BuildSendApiTxInput<KnownChainIds.SolanaMainnet>,
  ): Promise<SignTx<KnownChainIds.SolanaMainnet>> {
    try {
      const { from, accountNumber, to, chainSpecific, value } = input
      const { instructions = [], tokenId } = chainSpecific

      const { blockhash } = await this.connection.getLatestBlockhash()

      const computeUnitLimit = chainSpecific.computeUnitLimit
        ? Number(chainSpecific.computeUnitLimit)
        : undefined

      const computeUnitPrice = chainSpecific.computeUnitPrice
        ? Number(chainSpecific.computeUnitPrice)
        : undefined

      if (tokenId) {
        const tokenTransferInstructions = await this.buildTokenTransferInstructions({
          from,
          to,
          tokenId,
          value,
        })

        instructions.push(
          ...tokenTransferInstructions.map(instruction => this.convertInstruction(instruction)),
        )
      }

      const addressLookupTableAccountInfos = await this.getAddressLookupTableAccounts(
        chainSpecific.addressLookupTableAccounts ?? [],
      )

      return {
        addressNList: toAddressNList(this.getBip44Params({ accountNumber })),
        blockHash: blockhash,
        computeUnitLimit,
        computeUnitPrice,
        instructions,
        to: tokenId ? '' : to,
        value: tokenId ? '' : value,
        addressLookupTableAccountInfos,
      }
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.buildTransaction',
      })
    }
  }

  async buildSendTransaction(input: BuildSendTxInput<KnownChainIds.SolanaMainnet>): Promise<{
    txToSign: SignTx<KnownChainIds.SolanaMainnet>
  }> {
    try {
      const from = await this.getAddress(input)
      const txToSign = await this.buildSendApiTransaction({ ...input, from })

      return { txToSign: { ...txToSign, ...(input.pubKey ? { pubKey: input.pubKey } : {}) } }
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.buildTransaction',
      })
    }
  }

  async signTransaction(signTxInput: SignTxInput<SolanaSignTx>): Promise<string> {
    try {
      const { txToSign, wallet } = signTxInput

      if (!wallet) throw new Error('wallet is required')
      this.assertSupportsChain(wallet)

      await verifyLedgerAppOpen(this.chainId, wallet)

      const signedTx = await wallet.solanaSignTx(txToSign)

      if (!signedTx?.serialized) throw new Error('error signing tx')

      return signedTx.serialized
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.signTransaction',
      })
    }
  }

  async signAndBroadcastTransaction({
    senderAddress,
    receiverAddress,
    signTxInput,
  }: SignAndBroadcastTransactionInput<KnownChainIds.SolanaMainnet>): Promise<string> {
    try {
      const { txToSign, wallet } = signTxInput

      await Promise.all([
        assertAddressNotSanctioned(senderAddress),
        receiverAddress !== CONTRACT_INTERACTION && assertAddressNotSanctioned(receiverAddress),
      ])

      if (!wallet) throw new Error('wallet is required')
      this.assertSupportsChain(wallet)

      const tx = await wallet.solanaSendTx?.(txToSign)

      if (!tx) throw new Error('error signing & broadcasting tx')

      return tx.signature
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.signAndBroadcastTransaction',
      })
    }
  }

  async broadcastTransaction({
    senderAddress,
    receiverAddress,
    hex,
  }: BroadcastTransactionInput): Promise<string> {
    try {
      await Promise.all([
        assertAddressNotSanctioned(senderAddress),
        receiverAddress !== CONTRACT_INTERACTION && assertAddressNotSanctioned(receiverAddress),
      ])

      const txHash = await this.providers.http.sendTx({ sendTxBody: { hex } })

      return txHash
    } catch (err) {
      return handleBroadcastTransactionError(err)
    }
  }

  async getFeeData(
    input: GetFeeDataInput<KnownChainIds.SolanaMainnet>,
  ): Promise<FeeDataEstimate<KnownChainIds.SolanaMainnet>> {
    try {
      const { baseFee, fast, average, slow } = await this.providers.http.getPriorityFees()
      const { sendMax, chainSpecific } = input
      const { instructions } = chainSpecific

      const serializedTx = await this.buildEstimationSerializedTx(input)
      const baseComputeUnits = await this.providers.http.estimateFees({
        estimateFeesBody: { serializedTx },
      })

      const computeUnits = sendMax
        ? bnOrZero(baseComputeUnits).times(SOLANA_COMPUTE_UNITS_BUFFER_MULTIPLIER).toFixed()
        : baseComputeUnits

      // SOL transfers should have 0 instructions if they're askchually *just* a transfer (i.e "pure")
      // There *are* some complex SOL transfers, e.g Jupiter swaps, that have instructions on top of transfer,
      // and a lot of them at that
      // SPL/Jupiter transfers should use actual instruction count from passed instructions
      const isPureSolTransfer =
        !chainSpecific.tokenId && (!instructions || instructions.length === 0)
      const instructionCount = isPureSolTransfer ? 0 : instructions?.length ?? 0

      return {
        fast: {
          txFee: bn(microLamportsToLamports(fast))
            .times(computeUnits)
            .plus(bnOrZero(baseFee).times(instructionCount))
            .toFixed(),
          chainSpecific: { computeUnits, priorityFee: fast },
        },
        average: {
          txFee: bn(microLamportsToLamports(average))
            .times(computeUnits)
            .plus(bnOrZero(baseFee).times(instructionCount))
            .toFixed(),
          chainSpecific: { computeUnits, priorityFee: average },
        },
        slow: {
          txFee: bn(microLamportsToLamports(slow))
            .times(computeUnits)
            .plus(bnOrZero(baseFee).times(instructionCount))
            .toFixed(),
          chainSpecific: { computeUnits, priorityFee: slow },
        },
      }
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getFeeData',
      })
    }
  }

  // eslint-disable-next-line require-await
  async validateAddress(address: string): Promise<ValidAddressResult> {
    try {
      new PublicKey(address)
      return { valid: true, result: ValidAddressResultType.Valid }
    } catch (err) {
      return { valid: false, result: ValidAddressResultType.Invalid }
    }
  }

  async subscribeTxs(
    input: SubscribeTxsInput,
    onMessage: (msg: Transaction) => void,
    onError: (err: SubscribeError) => void,
  ): Promise<void> {
    const { pubKey, accountNumber, wallet } = input

    const bip44Params = this.getBip44Params({ accountNumber })
    const address = await this.getAddress({ accountNumber, wallet, pubKey })
    const subscriptionId = toRootDerivationPath(bip44Params)

    await this.providers.ws.subscribeTxs(
      subscriptionId,
      { topic: 'txs', addresses: [address] },
      async msg => onMessage(await this.parseTx(msg.data, msg.address)),
      err => onError({ message: err.message }),
    )
  }

  unsubscribeTxs(input?: SubscribeTxsInput): void {
    if (!input) return this.providers.ws.unsubscribeTxs()

    const { accountNumber } = input
    const bip44Params = this.getBip44Params({ accountNumber })
    const subscriptionId = toRootDerivationPath(bip44Params)

    this.providers.ws.unsubscribeTxs(subscriptionId, { topic: 'txs', addresses: [] })
  }

  closeTxs(): void {
    this.providers.ws.close('txs')
  }

  private async buildEstimationSerializedTx(
    input: GetFeeDataInput<KnownChainIds.SolanaMainnet>,
  ): Promise<string> {
    const { to, chainSpecific } = input
    const { from, tokenId, instructions } = chainSpecific

    const estimationInstructions = instructions
      ? instructions
      : await this.buildEstimationInstructions({
          from,
          to,
          tokenId,
          value: input.value,
        })

    const addressLookupTableAccounts = await this.getSolanaAddressLookupTableAccountsInfo(
      chainSpecific.addressLookupTableAccounts ?? [],
    )

    if (isUndefined(input.to)) throw new Error(`${this.getName()}ChainAdapter: to is required`)
    if (!input.value) throw new Error(`${this.getName()}ChainAdapter: value is required`)

    const message = new TransactionMessage({
      payerKey: new PublicKey(input.chainSpecific.from),
      instructions: estimationInstructions,
      // static block hash as fee estimation replaces the block hash with latest to save us a client side call
      recentBlockhash: '4sGjMW1sUnHzSxGspuhpqLDx6wiyjNtZAMdL4VZHirAn',
    }).compileToV0Message(addressLookupTableAccounts)

    const transaction = new VersionedTransaction(message)

    return Buffer.from(transaction.serialize()).toString('base64')
  }

  public async buildTokenTransferInstructions({
    from,
    to,
    tokenId,
    value,
  }: {
    from: string
    to: string
    tokenId: string
    value: string
  }): Promise<TransactionInstruction[]> {
    const instructions: TransactionInstruction[] = []

    const { instruction, destinationTokenAccount } =
      await this.createAssociatedTokenAccountInstruction({ from, to, tokenId })

    if (instruction) {
      instructions.push(instruction)
    }

    const accountInfo = await this.connection.getParsedAccountInfo(new PublicKey(tokenId))

    const isToken2022 = accountInfo?.value?.owner.toString() === TOKEN_2022_PROGRAM_ID.toString()

    if (isToken2022 && isToken2022AccountInfo(accountInfo.value?.data)) {
      instructions.push(
        createTransferCheckedInstruction(
          getAssociatedTokenAddressSync(
            new PublicKey(tokenId),
            new PublicKey(from),
            true,
            TOKEN_2022_PROGRAM_ID,
          ),
          new PublicKey(tokenId),
          destinationTokenAccount,
          new PublicKey(from),
          Number(value),
          accountInfo.value?.data.parsed?.info?.decimals ?? 0,
          [],
          TOKEN_2022_PROGRAM_ID,
        ),
      )
    } else {
      instructions.push(
        createTransferInstruction(
          getAssociatedTokenAddressSync(new PublicKey(tokenId), new PublicKey(from), true),
          destinationTokenAccount,
          new PublicKey(from),
          Number(value),
          undefined,
        ),
      )
    }

    return instructions
  }

  public async buildEstimationInstructions({
    from,
    to,
    tokenId,
    value,
    existingInstructions = [],
  }: {
    from: string
    to: string
    tokenId?: string
    value: string
    existingInstructions?: TransactionInstruction[]
  }): Promise<TransactionInstruction[]> {
    const estimationInstructions = [...existingInstructions]

    if (bnOrZero(value).gt(0)) {
      if (tokenId) {
        const tokenTransferInstructions = await this.buildTokenTransferInstructions({
          from,
          to,
          tokenId,
          value,
        })
        estimationInstructions.push(...tokenTransferInstructions)
      } else {
        estimationInstructions.push(
          SystemProgram.transfer({
            fromPubkey: new PublicKey(from),
            toPubkey: new PublicKey(to),
            lamports: Number(value),
          }),
        )
      }
    }

    // Only add compute budget instructions for SPL token operations
    if (tokenId) {
      estimationInstructions.push(
        ComputeBudgetProgram.setComputeUnitLimit({ units: MAX_COMPUTE_UNITS }),
        ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 0 }),
      )
    }

    return estimationInstructions
  }

  public async createAssociatedTokenAccountInstruction({
    from,
    to,
    tokenId,
  }: {
    from: string
    to: string
    tokenId: string
  }): Promise<{
    instruction?: TransactionInstruction
    destinationTokenAccount: PublicKey
  }> {
    const accountInfo = await this.connection.getAccountInfo(new PublicKey(tokenId))

    const TOKEN_PROGRAM =
      accountInfo?.owner.toString() === TOKEN_2022_PROGRAM_ID.toString()
        ? TOKEN_2022_PROGRAM_ID
        : TOKEN_PROGRAM_ID

    const destinationTokenAccount = getAssociatedTokenAddressSync(
      new PublicKey(tokenId),
      new PublicKey(to),
      true,
      TOKEN_PROGRAM,
    )

    // check if destination token account exists and add creation instruction if it doesn't
    try {
      await getAccount(this.connection, destinationTokenAccount, 'confirmed', TOKEN_PROGRAM)
    } catch (err) {
      if (
        err instanceof TokenAccountNotFoundError ||
        err instanceof TokenInvalidAccountOwnerError
      ) {
        return {
          instruction: createAssociatedTokenAccountInstruction(
            // sender pays for creation of the token account
            new PublicKey(from),
            destinationTokenAccount,
            new PublicKey(to),
            new PublicKey(tokenId),
            TOKEN_PROGRAM,
          ),
          destinationTokenAccount,
        }
      }
    }

    return {
      instruction: undefined,
      destinationTokenAccount,
    }
  }

  public convertInstruction(instruction: TransactionInstruction): SolanaTxInstruction {
    return {
      keys: instruction.keys.map(key => ({
        pubkey: key.pubkey.toString(),
        isSigner: key.isSigner,
        isWritable: key.isWritable,
      })),
      programId: instruction.programId.toString(),
      data: instruction.data,
    }
  }

  public async getTxStatus(tx: unchained.solana.Tx, pubkey: string): Promise<unchained.TxStatus> {
    const parsedTx = await this.parseTx(tx, pubkey)

    return parsedTx.status
  }

  async parseTx(tx: unchained.solana.Tx, pubkey: string): Promise<Transaction> {
    const { address: _, ...parsedTx } = await this.parser.parse(tx, pubkey)

    return {
      ...parsedTx,
      pubkey,
      transfers: parsedTx.transfers.map(transfer => ({
        assetId: transfer.assetId,
        from: [transfer.from],
        to: [transfer.to],
        type: transfer.type,
        value: transfer.totalValue,
      })),
    }
  }

  get httpProvider(): unchained.solana.Api {
    return this.providers.http
  }

  private async getAddressLookupTableAccountsInfo(
    addresses: string[],
  ): Promise<(AccountInfo<Buffer> | null)[]> {
    return await this.connection.getMultipleAccountsInfo(addresses.map(key => new PublicKey(key)))
  }

  private async getSolanaAddressLookupTableAccountsInfo(
    addresses: string[],
  ): Promise<AddressLookupTableAccount[]> {
    const addressLookupTableAccountInfos = await this.getAddressLookupTableAccountsInfo(addresses)

    return addressLookupTableAccountInfos.reduce((acc, accountInfo, index) => {
      const addressLookupTableAddress = addresses[index]
      if (accountInfo) {
        const addressLookupTableAccount = new AddressLookupTableAccount({
          key: new PublicKey(addressLookupTableAddress),
          state: AddressLookupTableAccount.deserialize(
            new Uint8Array(Buffer.from(accountInfo.data)),
          ),
        })
        acc.push(addressLookupTableAccount)
      }

      return acc
    }, new Array<AddressLookupTableAccount>())
  }

  public async getAddressLookupTableAccounts(
    addresses: string[],
  ): Promise<SolanaAddressLookupTableAccountInfo[]> {
    const addressLookupTableAccountInfos = await this.getAddressLookupTableAccountsInfo(addresses)

    return addressLookupTableAccountInfos.reduce((acc, accountInfo, index) => {
      const addressLookupTableAddress = addresses[index]
      if (accountInfo) {
        const addressLookupTableAccount = {
          key: addressLookupTableAddress,
          data: Buffer.from(accountInfo.data),
        }
        acc.push(addressLookupTableAccount)
      }

      return acc
    }, new Array<SolanaAddressLookupTableAccountInfo>())
  }
}
